import { useState, useRef, useEffect } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { MultiFilterSelect } from 'ui/selects';
import { sortRows } from 'ui/table/utils';
import { ASCENDING } from 'constants/sortDirections';
import { Menu } from 'ui/menu';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import { Overflow } from 'ui/icons';
import { Table } from 'ui/table/Table';
import { ALIGNMENT } from 'ui/menu/constants';
import { CampaignMetricLabels, OpenMetrics } from 'modules/messagingInsights/constants';
import { sortBySelectionOrder } from 'modules/productAnalytics/utils';
import { HiddenCSVLink, StyledDivider } from 'modules/productAnalytics/Styled';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import { LayoutTemplate } from './LayoutTemplate';
import { TimeSeriesChart } from './TimeSeriesChart';
import { NoDataCard } from './NoDataCard';
import {
  parseMetricsForTimeSeriesGraph,
  parseMetricsForTimeSeriesTable,
  parseMetricsForTimeSeriesDropdown,
} from '../utils';
import {
  selectCampaignAnalyticsStatsForTimerange,
  selectIsLoading,
  selectMessageMedium,
  selectAnalyticsMetricsOptions,
  selectAnalyticsMetricsValueTypes,
} from '../selectors';
import {
  StyledSelectLabel,
  SelectWrapper,
  FilterCard,
  StyledCampaignAnalyticsCard,
  CampaignAnalyticsCardHeader,
  StyledRows,
  StyledHeader,
  TableWrapper,
} from './Styled';
import { TIME_SERIES_AND_SEGMENTS_DEFAULT_METRICS, loadingRows, loadingCols } from '../constants';
import { useFiltersContext, useTimeFiltersContext } from '../context';

export const CampaignAnalyticsTimeSeries = () => {
  const csvLink = useRef();
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const isLoading = useSelector(selectIsLoading);
  const messageMedium = useSelector(selectMessageMedium);
  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);

  const { timeSeriesMetrics: selectedMetrics, setTimeSeriesMetrics: setSelectedMetrics } =
    useFiltersContext();
  const MetricsOptions = useSelector(selectAnalyticsMetricsOptions) || [];
  const [timeSeriesSortedBy, setTimeSeriesSortedBy] = useState('dateTime');
  const [timeSeriesSortDirection, setTimeSeriesSortDirection] = useState(ASCENDING);
  const { timezone } = useTimeFiltersContext();

  const selectedMetricsNiceNames = selectedMetrics.map((metric) => METRIC_VALUES_TO_LABELS[metric]);

  const filteredMetricsOptions = parseMetricsForTimeSeriesDropdown(
    MetricsOptions || [],
    statsForTimeRange?.results,
  );
  const timeSeriesGraphData = parseMetricsForTimeSeriesGraph(
    selectedMetricsNiceNames,
    statsForTimeRange?.results,
  );

  const { columns, rows } = parseMetricsForTimeSeriesTable(
    selectedMetricsNiceNames,
    statsForTimeRange?.results,
    valueTypes,
  );

  const sortedRows = sortRows(rows, timeSeriesSortedBy, timeSeriesSortDirection);
  const overflowActions = {
    'export-timeseries-table': () => csvLink.current.link.click(),
  };

  const isEqualMetricLists = (arr1, arr2) => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sortedArr1 = arr1.sort();
    const sortedArr2 = arr2.sort();
    for (let i = 0; i < sortedArr1.length; i += 1) {
      if (sortedArr1[i] !== sortedArr2[i]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (messageMedium && !selectedMetrics.length) {
      setSelectedMetrics(TIME_SERIES_AND_SEGMENTS_DEFAULT_METRICS[messageMedium]);
    }
  }, [messageMedium]);

  return (
    <LayoutTemplate
      pageName="Time series"
      canShowFPOBanner={selectedMetrics.some((metric) => OpenMetrics.includes(metric))}
    >
      <FilterCard>
        {MetricsOptions.length > 0 && (
          <div>
            <StyledSelectLabel>Select metrics</StyledSelectLabel>
            <SelectWrapper>
              <MultiFilterSelect
                content={{
                  clearText: 'Clear',
                  selectNone: 'Select none',
                  selectAll: 'Select all',
                }}
                defaultValues={selectedMetrics}
                placeholder={'0 types of metrics'}
                inputContent={{ filterPlaceholder: 'Search all metrics' }}
                displayContent={{
                  multipleSelectedText: 'types of metrics',
                }}
                onSelect={(val) => {
                  if (!isEqualMetricLists(val, selectedMetrics)) {
                    const sortedVal = sortBySelectionOrder(selectedMetrics || [], val);
                    setSelectedMetrics(sortedVal);
                  }
                }}
                id={`campaign-metrics`}
                title={`campaign-metrics`}
                options={filteredMetricsOptions}
                categoryLabels={CampaignMetricLabels}
              />
            </SelectWrapper>
          </div>
        )}
      </FilterCard>
      {selectedMetrics.length > 0 ? (
        <>
          <StyledCampaignAnalyticsCard>
            <TimeSeriesChart
              data={timeSeriesGraphData}
              isLoading={isLoading}
              id={'campaignAnalyticsTimeseries'}
              timezone={timezone}
            />
          </StyledCampaignAnalyticsCard>
          <StyledCampaignAnalyticsCard>
            <CampaignAnalyticsCardHeader>
              Table
              <Menu
                handleSelectionChange={(id) => id && overflowActions[id]()}
                horizontalAlignment={ALIGNMENT.RIGHT}
                options={[{ label: 'Export table to csv', id: 'export-timeseries-table' }]}
              >
                <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
                  <Overflow />
                </IconButton>
              </Menu>
              <HiddenCSVLink
                data={sortedRows}
                filename={'timeseries_table.csv'}
                ref={csvLink}
                target="_blank"
              />
            </CampaignAnalyticsCardHeader>
            <StyledDivider />
            {isLoading ? (
              <TableWrapper>
                <Table>
                  <StyledHeader columns={loadingCols} isSticky />
                  <StyledRows rows={loadingRows} idKey="loading" columns={loadingCols} />
                </Table>
              </TableWrapper>
            ) : (
              <TableWrapper>
                <Table>
                  <StyledHeader
                    columns={columns}
                    sortedBy={timeSeriesSortedBy}
                    setSortedBy={setTimeSeriesSortedBy}
                    sortDirection={timeSeriesSortDirection}
                    setSortDirection={setTimeSeriesSortDirection}
                    isSticky
                  />
                  <StyledRows rows={sortedRows} idKey="dateTime" columns={columns} />
                </Table>
              </TableWrapper>
            )}
          </StyledCampaignAnalyticsCard>
        </>
      ) : (
        <StyledCampaignAnalyticsCard>
          <NoDataCard
            title="No metrics selected"
            subtitle="Select a metric from the dropdown above to view time series data."
          />
        </StyledCampaignAnalyticsCard>
      )}
    </LayoutTemplate>
  );
};

export default withRouter(CampaignAnalyticsTimeSeries);
